import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { IdCard } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { fetchGroups, type AdminGroupRow } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { GROUP_OPS_STR as S } from './groupOpsApi'

/**
 * A4 amal-ekranlari uchun umumiy ro'yxat: JONLI `/admin/groups` (dialogs WHERE
 * type IN group,channel) + bitta guruh-detal amali qator-menyusida. Amalning
 * o'zi (`run`) so'rovnoma/tasdiqni O'ZI boshqaradi va success/cancel qaytaradi;
 * xato → toast (mutatsiya endpointi hali yo'q bo'lsa ham UI yiqilmaydi).
 */
export interface GroupActionListProps {
  screenId: string
  title: string
  description?: string
  actionId: string
  actionLabel: string
  actionIcon: ReactNode
  emptyIcon: ReactNode
  successMsg: string
  danger?: boolean
  permission?: string
  /** true → muvaffaqiyat (toast+reload); false → bekor qilindi (jim); throw → xato. */
  run: (row: AdminGroupRow) => Promise<boolean>
}

export function GroupActionList(props: GroupActionListProps) {
  const t = useT()
  const { toast } = useToast()
  const navigate = useNavigate()
  const [reloadKey, setReloadKey] = useState(0)

  const perform = useCallback(
    async (row: AdminGroupRow) => {
      try {
        const ok = await props.run(row)
        if (!ok) return
        toast(props.successMsg, { tone: 'success' })
        setReloadKey((k) => k + 1)
      } catch (e) {
        toast(S.actionFailed, {
          tone: 'error',
          ...(e instanceof Error && e.message ? { description: e.message } : {}),
        })
      }
    },
    [props, toast],
  )

  const rowActions = useMemo<RowAction<AdminGroupRow>[]>(
    () => [
      {
        id: 'card',
        label: t.users.viewCard,
        icon: <IdCard size={13} />,
        onSelect: (r) => navigate(`/groups/${r.id}`),
      },
      {
        id: props.actionId,
        label: props.actionLabel,
        icon: props.actionIcon,
        danger: props.danger,
        permission: props.permission,
        onSelect: (r) => void perform(r),
      },
    ],
    [t, navigate, props, perform],
  )

  const columns = useMemo<AdminColumn<AdminGroupRow>[]>(
    () => [
      {
        key: 'type',
        header: t.col.type,
        type: 'enum',
        width: 110,
        options: [
          { value: 'group', label: t.groups.group, status: 'active' },
          { value: 'channel', label: t.groups.channel, status: 'review' },
        ],
      },
      { key: 'title', header: t.col.title, type: 'text', minWidth: 200 },
      { key: 'username', header: t.col.username, type: 'text', width: 160 },
      { key: 'member_count', header: t.groups.members, type: 'number', width: 110 },
      { key: 'is_forum', header: t.groups.forum, type: 'bool', width: 90 },
      { key: 'created_at', header: t.col.registered, type: 'date', width: 150 },
    ],
    [t],
  )

  const fetcher = useCallback(fetchGroups, [])

  return (
    <>
      <PageHeader title={props.title} screenId={props.screenId} description={props.description} />
      <DataTable<AdminGroupRow>
        key={reloadKey}
        screenId={props.screenId}
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: props.emptyIcon }}
      />
    </>
  )
}
